import { useContext, useEffect, useMemo, useState } from 'react';

import { replaceStringDotsWithUnderscores } from '@/utils/array';

import type { ProductType } from '@/integrations/algolia/helpers';
import {
  applyProductConfigurationFilters,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import {
  AI_RECOMMENDATION_MAX_ITEMS,
  AI_RECOMMENDATION_THRESHOLD,
} from '@/lib/const';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';

const searchClient = createSearchClient();

interface AlgoliaHit {
  objectID: string;
}

interface UseAIRecommendationsProps {
  currentProductSku: string | undefined;
  pimSlugs: string[];
  productType: ProductType;
}

interface UseAIRecommendationsResult {
  isLoading: boolean;
  mergedSlugs: string[];
}

export const useAIRecommendations = ({
  currentProductSku,
  pimSlugs,
  productType,
}: UseAIRecommendationsProps): UseAIRecommendationsResult => {
  const locale = useContext(LocaleContext);
  const draftMode = useContext(DraftModeContext);

  const shouldFetch = !!currentProductSku && !!locale;

  const [hits, setHits] = useState<AlgoliaHit[]>([]);
  const [isLoading, setIsLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!currentProductSku || !locale) {
      setHits([]);
      return;
    }

    let cancelled = false;

    const fetchRecommendations = async () => {
      setIsLoading(true);

      const filters = applyProductConfigurationFilters(
        ProductConfigurationFilterType.COMPONENTS,
        productType,
        draftMode,
      );
      try {
        const response = await searchClient.getRecommendations([
          {
            indexName: getIndexName('PRODUCT', locale as string),
            objectID: currentProductSku,
            model: 'looking-similar',
            threshold: AI_RECOMMENDATION_THRESHOLD,
            maxRecommendations: AI_RECOMMENDATION_MAX_ITEMS,
            queryParameters: {
              filters: filters || undefined,
            },
          },
        ]);

        if (!cancelled) {
          setHits((response.results[0]?.hits ?? []) as AlgoliaHit[]);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[useAIRecommendations] Algolia request failed:', err);
          setHits([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchRecommendations();

    return () => {
      cancelled = true;
    };
  }, [currentProductSku, productType, locale, draftMode]);

  const mergedSlugs = useMemo(() => {
    if (isLoading) return [];

    const aiSlugs = hits.map(({ objectID }: AlgoliaHit) =>
      replaceStringDotsWithUnderscores(objectID),
    );

    const aiSlugSet = new Set(aiSlugs);
    const pimFallbackSlugs = pimSlugs.filter((slug) => !aiSlugSet.has(slug));

    return [...aiSlugs, ...pimFallbackSlugs];
  }, [hits, pimSlugs, isLoading]);

  return { mergedSlugs, isLoading };
};
